import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { invoiceService, InvoiceRequestError, type InvoiceCatalogProduct } from '../services/invoiceService';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (product: InvoiceCatalogProduct) => void;
}

type Field = 'sku' | 'name' | 'bbd_price';
type Values = { sku: string; name: string; bbd_price: string; fulfilment_type: 'buy' | 'produce' };
const empty: Values = { sku: '', name: '', bbd_price: '', fulfilment_type: 'buy' };

export const CreateInvoiceCatalogDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [values, setValues] = useState<Values>(empty);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const skuRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) skuRef.current?.focus();
  }, [open]);

  const close = () => {
    if (saving) return;
    setValues(empty); setErrors({}); setServerError('');
    onClose();
  };
  const change = <K extends keyof Values>(field: K, value: Values[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setServerError('');
  };
  const focus = (field: Field) => {
    const ref = { sku: skuRef, name: nameRef, bbd_price: priceRef }[field];
    requestAnimationFrame(() => ref.current?.focus());
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const next: Partial<Record<Field, string>> = {};
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{2,49}$/.test(values.sku.trim())) next.sku = 'Use 3–50 letters, numbers, dashes or underscores.';
    if (values.name.trim().length < 3 || values.name.trim().length > 255) next.name = 'Use a product name of 3–255 characters.';
    if (!/^\d+(?:\.\d{1,2})?$/.test(values.bbd_price) || Number(values.bbd_price) <= 0) next.bbd_price = 'Enter a BBD price above zero, with at most two decimal places.';
    setErrors(next);
    const first = (Object.keys(next) as Field[])[0];
    if (first) { focus(first); return; }

    setSaving(true); setServerError('');
    try {
      const product = await invoiceService.createCatalogProduct({
        sku: values.sku.trim().toUpperCase(), name: values.name.trim(), bbd_price: Number(values.bbd_price).toFixed(2),
        fulfilment_type: values.fulfilment_type,
        hidden_from_shop: true,
      });
      setValues(empty); setErrors({});
      onCreated(product);
      onClose();
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Could not create product. Please try again.';
      const code = reason instanceof InvoiceRequestError ? reason.code : null;
      const field: Field | null = code?.includes('sku') || /duplicate sku/i.test(message) ? 'sku'
        : code?.includes('name') ? 'name'
          : code?.includes('price') ? 'bbd_price'
            : null;
      if (field) {
        setErrors((current) => ({ ...current, [field]: message }));
        focus(field);
      } else setServerError(message);
    } finally { setSaving(false); }
  };

  return <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="invoice-catalog-title">
    <DialogTitle id="invoice-catalog-title">Create inventory product</DialogTitle>
    <form onSubmit={submit} noValidate>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">The BBD price is saved as this product's public Price in Shop Admin, before VAT. Inventory Unit Price is a separate cost/value field and is not set here. USD invoices need a separate USD unit price. The item starts with zero stock and stays hidden from the public shop until separately published. One-off invoice lines do not enter production.</Typography>
          {serverError && <Alert severity="error" role="alert">{serverError}</Alert>}
          <TextField label="SKU" value={values.sku} onChange={(event) => change('sku', event.target.value)} inputRef={skuRef} error={Boolean(errors.sku)} helperText={errors.sku} required fullWidth autoFocus />
          <TextField label="Product name" value={values.name} onChange={(event) => change('name', event.target.value)} inputRef={nameRef} error={Boolean(errors.name)} helperText={errors.name} required fullWidth />
          <TextField label="Shop Admin Price (BBD before VAT)" value={values.bbd_price} onChange={(event) => change('bbd_price', event.target.value)} inputRef={priceRef} error={Boolean(errors.bbd_price)} helperText={errors.bbd_price} type="number" inputProps={{ min: 0.01, step: 0.01 }} required fullWidth />
          <TextField select label="Inventory type" value={values.fulfilment_type} onChange={(event) => change('fulfilment_type', event.target.value as Values['fulfilment_type'])} fullWidth>
            <MenuItem value="buy">Buy — physical stock required</MenuItem>
            <MenuItem value="produce">Produce — make to order</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={close} disabled={saving}>Cancel</Button><Button type="submit" variant="contained" disabled={saving}>{saving ? 'Creating…' : 'Create and add to invoice'}</Button></DialogActions>
    </form>
  </Dialog>;
};
